import { useRef, useEffect, useCallback } from "react";

import { AuthRoute, useRouteMatch, useParams, Redirect, useQueryParameters } from "extensions/react-router";
import Radio from "backbone.radio";

import { ensureErrorCallback } from "common/components/error";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";

import { PreferencesOverlay } from "./preferencesOverlay";

const makeDisplayOptionsInstance = (str) => new Qualifiers({
    value: str,
    whitelisted: ["tab", "id", "highlight", "unmapped"]
});

/**
 * Show profile info page
 */
function Profile({ getOverlay }) {
    useEffect(() => {
        async function main() {
            try {
                const { ProfileRedux } = await import("./profile");

                getOverlay().showContent(<ProfileRedux/>, {
                    name: "Profile"
                });
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        main();
    }, [getOverlay]);

    return null;
}

function Preferences({ getOverlay }) {
    useEffect(() => {
        async function main() {
            try {
                const { PreferencesRedux } = await import("./preferences");

                getOverlay().showContent(<PreferencesRedux/>, {
                    name: "Preferences"
                });
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        main();
    }, [getOverlay]);

    return null;
}

function Security({ getOverlay }) {
    useEffect(() => {
        async function main() {
            try {
                const { SignInAndSecurityView } = await import("./security/signInAndSecurity");
                const security = new SignInAndSecurityView();

                security.on("hasUnsavedChanges", (value) => {
                    getOverlay().hasUnsavedChanges.security = value;
                });

                getOverlay().showContent(security);
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        main();
    }, [getOverlay]);

    return null;
}

function Users({ getOverlay }) {
    const { tab, id } = useParams();
    const queryParams = useQueryParameters();

    useEffect(() => {
        async function main() {
            try {
                const { UserManagementRedux } = await import("merchant/features/userManagement/userManagement");

                // We still support old qualifiers here

                let activeTab;
                let activeId;
                if (Qualifiers.isValid(tab)) {
                    const q = makeDisplayOptionsInstance(tab);
                    activeTab = q.get("tab")[0];
                    activeId = q.get("id")[0];
                } else {
                    activeTab = tab;
                    activeId = id;
                }

                getOverlay().showContent(
                    <UserManagementRedux
                        tab={activeTab}
                        id={activeId}
                        tableOptions={queryParams}
                    />,
                    { name: "User management", className: "user-management", waitForAttach: true }
                );
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        main();
    }, [tab, id, queryParams, getOverlay]);
    return null;
}

function Data({ getOverlay }) {
    const { datetype: tabView = "report", month, qualifiers: options } = useParams();
    useEffect(() => {
        async function main() {
            try {
                const { DataStatus } = await import("merchant/features/preferences/data-react");
                getOverlay().showContent(<DataStatus tabView={tabView} opts={options || ""} month={month || ""} getOverlay={getOverlay} />, {
                    className: "data-status-2",
                    name: "Data status"
                });
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        main();
    }, [tabView, month, options, getOverlay]);
    return null;
}

function Bumanager({ getOverlay }) {
    const { options } = useParams();
    useEffect(() => {
        async function main() {
            try {
                const { BusinessUnitManager } = await import("merchant/features/businessUnitManager/");
                getOverlay().showContent(new BusinessUnitManager({
                    startOnUnmapped: options === "unmapped"
                }));
            } catch (error) {
                ensureErrorCallback(error);
            }
        }
        main();
    }, [options, getOverlay]);
    return null;
}

export function PreferencesRouter() {
    const { url } = useRouteMatch();
    const overlay = useRef(null);

    /**
     * Reuse existing account layout if it exists
     * @returns {null|*}
     */
    const getOverlay = useCallback(() => {
        Radio.channel("main").request("ensure:background:content");
        if (!overlay.current) {
            overlay.current = new PreferencesOverlay();
            overlay.current.on("destroy", () => {
                overlay.current = null;
            });
            overlay.current.render();
        }
        return overlay.current;
    }, [overlay]);

    useEffect(() => {
        const closeActiveView = () => {
            if (overlay.current) {
                overlay.current.closeOverlay();
                overlay.current = null;
            }
        };

        Radio.channel("account").on("close:overlays", closeActiveView);
        return () => Radio.channel("account").off("close:overlays", closeActiveView);
    }, []);

    return (
        <>
            <AuthRoute path={`${url}/account/:section?`}>
                <Redirect to={`${url}/preferences/profile`}/>
            </AuthRoute>
            <AuthRoute path={`${url}/preferences/profile`}>
                <Profile getOverlay={getOverlay}/>
            </AuthRoute>
            <AuthRoute authenticated={(state) => state.permissions.user_permissions.feature_fee_reports} path={`${url}/preferences/preferences`}>
                <Preferences getOverlay={getOverlay}/>
            </AuthRoute>
            <AuthRoute path={`${url}/preferences/security`}>
                <Security getOverlay={getOverlay}/>
            </AuthRoute>
            <AuthRoute authenticated={(state) => state.permissions.user_permissions.can_view_users} path={`${url}/preferences/users/:tab?/:id?`}>
                <Users getOverlay={getOverlay}/>
            </AuthRoute>
            
            <AuthRoute path={`${url}/preferences/data/:datetype?/:month?/:qualifiers?`}>
                <Data getOverlay={getOverlay}/>
            </AuthRoute>
            <AuthRoute path={`${url}/preferences/bumanager/:options?`}>
                <Bumanager getOverlay={getOverlay}/>
            </AuthRoute>
        </>
    );
}
